import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { BehaviorSubject, Observable, Subject, filter, map, takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';

export class Claim {
  id: number = 0;
  claim: string = '';
  value: string = '';
}

export const ROLE_ADMIN = 'Administrator';
export const ROLE_AZUBI = 'Azubi';
export const ROLE_DISPO = 'Disponent';
export const ROLE_OM = 'OperationsManagement';
export const ROLE_FLEET = 'Fuhrparkleitung';
export const ROLE_DRIVER = 'Fahrer';
export const ROLE_UNKNOWN = 'Unknown';

export type Role =
  | typeof ROLE_ADMIN
  | typeof ROLE_AZUBI
  | typeof ROLE_DISPO
  | typeof ROLE_OM
  | typeof ROLE_FLEET
  | typeof ROLE_DRIVER
  | typeof ROLE_UNKNOWN;

export function toRole(role: string): Role {
  switch (role) {
    case ROLE_ADMIN:
      return ROLE_ADMIN;
    case ROLE_AZUBI:
      return ROLE_AZUBI;
    case ROLE_DISPO:
      return ROLE_DISPO;
    case ROLE_OM:
      return ROLE_OM;
    case ROLE_FLEET:
      return ROLE_FLEET;
    case ROLE_DRIVER:
      return ROLE_DRIVER;
    default:
      return ROLE_UNKNOWN;
  }
}

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private _loginState$ = new BehaviorSubject(false);
  private _oid$ = new BehaviorSubject<string>('');
  private _name$ = new BehaviorSubject<string>('');
  private _roles$ = new BehaviorSubject<Role[]>([]);
  private _silentLoginInProgress$ = new BehaviorSubject(false);
  private _rolesInitialized$ = new BehaviorSubject<boolean>(false);
  private readonly _destroying$ = new Subject<void>();
  private _init$ = new BehaviorSubject<boolean>(false);

  private msalBroadcastService = inject(MsalBroadcastService);
  private authService = inject(MsalService);
  private router = inject(Router);

  get rolesInitialized$(): Observable<boolean> {
    return this._rolesInitialized$.asObservable();
  }

  get isInit(): boolean {
    return this._init$.value;
  }

  get isLoggedIn$(): Observable<boolean> {
    return this._loginState$.asObservable();
  }

  get isLoggedIn(): boolean {
    return this._loginState$.value;
  }

  get getOid$(): Observable<string> {
    return this._oid$.asObservable();
  }

  get getOid(): string {
    return this._oid$.value;
  }

  get getName$(): Observable<string> {
    return this._name$.asObservable();
  }

  get getName(): string {
    return this._name$.value;
  }

  get isAdmin(): boolean {
    return this.hasRole(ROLE_ADMIN);
  }

  get isDispo(): boolean {
    return this.hasRole(ROLE_DISPO);
  }

  get isAzubi(): boolean {
    return this.hasRole(ROLE_AZUBI);
  }

  get isFleet(): boolean {
    return this.hasRole(ROLE_FLEET);
  }

  get isOM(): boolean {
    return this.hasRole(ROLE_OM);
  }

  hasRole$(role: Role): Observable<boolean> {
    return this._roles$.pipe(map(roles => roles.includes(role)));
  }

  hasRole(role: Role): boolean {
    return this._roles$.value.includes(role);
  }

  get getRoles$(): Observable<Array<Role>> {
    return this._roles$.asObservable();
  }

  get getRoles(): Array<Role> {
    return this._roles$.value;
  }

  get isSilentLoginInProgress$(): Observable<boolean> {
    return this._silentLoginInProgress$.asObservable();
  }

  constructor() {
    this._init$.next(true);

    this.authService.instance.enableAccountStorageEvents();

    // Keep local state in sync when login/logout happens in this or another tab
    this.msalBroadcastService.msalSubject$
      .pipe(takeUntil(this._destroying$))
      .subscribe((message: EventMessage) => {
        switch (message.eventType) {
          case EventType.LOGIN_SUCCESS:
          case EventType.SSO_SILENT_SUCCESS:
          case EventType.ACCOUNT_ADDED:
            this.checkAndSetActiveAccount();
            void this.updateLoginState();
            break;

          case EventType.LOGOUT_SUCCESS:
          case EventType.ACCOUNT_REMOVED:
            this.clearSessionState();
            break;
        }
      });

    // Only touch account/login state once MSAL is no longer busy
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
        void this.updateLoginState();
      });

    // Initialize state from existing cached account on page refresh
    this.checkAndSetActiveAccount();
    void this.updateLoginState();
  }

  async updateLoginState(): Promise<void> {
    if (!this._init$.value) return;

    const activeAccount = this.authService.instance.getActiveAccount();
    const isLoggedIn = !!activeAccount;

    if (!isLoggedIn) {
      this.clearSessionState();
      this._rolesInitialized$.next(true);
      return;
    }

    const claims = this.getClaims(activeAccount.idTokenClaims);

    const oid = claims.find(c => c.claim === 'oid');
    this._oid$.next(oid?.value ?? '');

    const roles = claims.find(c => c.claim === 'roles');
    if (roles?.value && Array.isArray(roles.value) && roles.value.every(item => typeof item === 'string')) {
      this._roles$.next((roles.value as string[]).map(toRole));
    } else {
      this._roles$.next([]);
    }

    const name = claims.find(c => c.claim === 'name');
    this._name$.next(name?.value ?? '');

    this._rolesInitialized$.next(true);
    this._silentLoginInProgress$.next(false);
    this._loginState$.next(true);
  }

  checkAndSetActiveAccount(): void {
    const activeAccount = this.authService.instance.getActiveAccount();
    if (activeAccount) {
      return;
    }

    const accounts = this.authService.instance.getAllAccounts();
    if (accounts.length > 0) {
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  async login(returnUrl?: string): Promise<void> {
  this._silentLoginInProgress$.next(true);

  await this.authService.loginRedirect({
    scopes: [...environment.azureActiveDirectory.scopes],
    redirectStartPage: returnUrl ?? this.router.url,
  });
}

  async logout(): Promise<void> {
    this.clearSessionState();

    await this.authService.logoutRedirect({
      postLogoutRedirectUri: `${window.location.origin}/redirect`,
    });
  }

  private clearSessionState(): void {
  this._oid$.next('');
  this._name$.next('');
  this._roles$.next([]);
  this._rolesInitialized$.next(true);
  this._silentLoginInProgress$.next(false);
  this._loginState$.next(false);
}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getClaims(claims: any): Claim[] {
    if (claims == null) return [];

    const list: Claim[] = [];
    Object.keys(claims).forEach((k, v) => {
      const c = new Claim();
      c.id = v;
      c.claim = k;
      c.value = claims[k];
      list.push(c);
    });

    return list;
  }

  getLanguage(): string {
    return '';
  }

  async setLanguage(_: string): Promise<void> {}

  async redirectWithForcedReload(url: string): Promise<boolean> {
    return this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      return this.router.navigateByUrl(url);
    });
  }
}
